import { createAsyncThunk, createReducer } from '@reduxjs/toolkit';
import { API } from '../../constants';
import { application } from '../../services/application';

export const INITIAL_STATE = {
  loading: false,
  refreshing: false,
  error: null,
  orgId: null,
  requestId: null,
  generatedAt: null,
  profile: null,
  matchingCriteria: { naics: [], psc: [], setAsides: [], serviceRegions: [] },
  categories: {
    federal_opportunities: { status: 'empty', reason: null, items: [] },
    sled_opportunities: { status: 'empty', reason: null, items: [] },
    sled_forecasts: { status: 'empty', reason: null, items: [] },
    vendors: { status: 'empty', reason: null, items: [] },
  },
  // NEI (APP-11251): the single hard gate is NAICS. Default hasNaics=true so older
  // responses without profileStatus degrade to the populated path.
  hasNaics: true,
  profileSufficient: true,
  profileFields: [],
  profileFieldStatus: {},
  completedCount: 0,
  requiredCount: 4,
  // Daily-limit resilience: served-stale signalling.
  servedStale: false,
  limitReached: false,
  lastRefreshedAt: null,
};

/**
 * Build a { fieldKey: boolean } lookup from the backend profileStatus.fields list.
 * @param {Array<{ key: string, complete: boolean }>} fields
 * @returns {Object<string, boolean>}
 */
export function mapFieldStatus(fields = []) {
  return (Array.isArray(fields) ? fields : []).reduce((acc, field) => {
    if (field?.key) acc[field.key] = Boolean(field.complete);
    return acc;
  }, {});
}

export const fetchRecommendations = createAsyncThunk('recommendations/fetch', async (params, { rejectWithValue }) => {
  try {
    return await application.call(API.RECOMMENDATIONS.GET, params || {});
  } catch (error) {
    return rejectWithValue(error?.message || 'Failed to load recommendations');
  }
});

function applyRecommendations(state, action) {
  const payload = action.payload || {};
  state.loading = false;
  state.refreshing = false;
  state.error = null;
  state.orgId = payload.org_id || null;
  state.requestId = payload.request_id || null;
  state.generatedAt = payload.generated_at || null;
  state.profile = payload.profile || null;
  state.matchingCriteria = {
    naics: payload.matchingCriteria?.naics || [],
    psc: payload.matchingCriteria?.psc || [],
    setAsides: payload.matchingCriteria?.setAsides || [],
    serviceRegions: payload.matchingCriteria?.serviceRegions || [],
  };
  state.categories = payload.categories
    ? { ...INITIAL_STATE.categories, ...payload.categories }
    : { ...INITIAL_STATE.categories };

  const profileStatus = payload.profileStatus;
  if (profileStatus) {
    state.hasNaics = Boolean(profileStatus.hasNaics);
    state.profileSufficient = Boolean(profileStatus.sufficient ?? profileStatus.hasNaics);
    state.profileFields = Array.isArray(profileStatus.fields) ? profileStatus.fields : [];
    state.profileFieldStatus = mapFieldStatus(profileStatus.fields);
    state.completedCount = Number(profileStatus.completedCount || 0);
    state.requiredCount = Number(profileStatus.requiredCount || INITIAL_STATE.requiredCount);
  } else {
    // Back-compat: no profileStatus ⇒ keep the populated path.
    state.hasNaics = true;
    state.profileSufficient = true;
    state.profileFields = [];
    state.profileFieldStatus = {};
    state.completedCount = 0;
    state.requiredCount = INITIAL_STATE.requiredCount;
  }

  const meta = payload.meta || {};
  state.servedStale = Boolean(meta.servedStale);
  state.limitReached = Boolean(meta.limitReached);
  state.lastRefreshedAt = meta.fetchedAt || null;
}

export default createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase(fetchRecommendations.pending, (state, action) => {
      const isRefresh = Boolean(action.meta?.arg?.refresh);
      state.loading = !isRefresh;
      state.refreshing = isRefresh;
      state.error = null;
    })
    .addCase(fetchRecommendations.fulfilled, applyRecommendations)
    .addCase(fetchRecommendations.rejected, (state, action) => {
      state.loading = false;
      state.refreshing = false;
      state.error = action.payload || action.error?.message || 'Failed to load recommendations';
    });
});
